import type { ExpressCheckoutWallet } from '../types/elements';
import { APPLE_PAY_OVERLAY_MARK_SVG, GOOGLE_PAY_OVERLAY_MARK_SVG } from './wallet-brand-marks';

const FALLBACK_Z_INDEX = 2_147_483_646;
const PROVIDER_TEXT_GAP = '16px';
const PROVIDER_MARK_CANVAS_ASPECT_RATIO = '1094 / 742';
// Match the Apple mark's visible width to the Google outline inside its official canvas.
const APPLE_PAY_MARK_WIDTH_IN_SHARED_CANVAS = '68.74%';
const APPLE_PAY_TITLE_MARGIN = '8px';
const GOOGLE_PAY_TITLE_MARGIN = '2px';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

type IsolatedSibling = {
  element: HTMLElement;
  inert: boolean;
  ariaHidden: string | null;
  tabIndex: string | null;
};

type ActiveWalletAttempt = {
  owner: ElementsWalletAttemptOverlay;
  componentId: string;
  requestId: string;
};

type WalletAttemptOverlayPhase = 'provider_open' | 'processing';

let activeWalletAttempt: ActiveWalletAttempt | null = null;

const restoreAttribute = (element: HTMLElement, name: string, value: string | null): void => {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
};

const walletName = (wallet: ExpressCheckoutWallet): string =>
  wallet === 'apple_pay' ? 'Apple Pay' : 'Google Pay';

const createWalletMark = (wallet: ExpressCheckoutWallet): SVGElement => {
  const template = document.createElement('template');
  template.innerHTML =
    wallet === 'apple_pay' ? APPLE_PAY_OVERLAY_MARK_SVG : GOOGLE_PAY_OVERLAY_MARK_SVG;
  const mark = template.content.firstElementChild;
  if (!(mark instanceof SVGElement)) {
    throw new Error('Unable to create the wallet provider mark.');
  }
  mark.removeAttribute('style');
  mark.setAttribute('aria-hidden', 'true');
  mark.setAttribute('focusable', 'false');
  mark.dataset.paypercutWalletMark = wallet;
  Object.assign(mark.style, {
    display: 'block',
    flex: 'none',
    width: wallet === 'apple_pay' ? APPLE_PAY_MARK_WIDTH_IN_SHARED_CANVAS : '100%',
    maxWidth: '100%',
    height: wallet === 'apple_pay' ? 'auto' : '100%',
  });
  return mark;
};

const createProcessingIndicator = (): HTMLDivElement => {
  const progress = document.createElement('div');
  progress.dataset.paypercutWalletProgress = '';
  progress.setAttribute('role', 'progressbar');
  progress.setAttribute('aria-label', 'Processing payment');
  progress.setAttribute('aria-valuetext', 'Processing');
  Object.assign(progress.style, {
    width: '28px',
    height: '28px',
    marginTop: '20px',
    display: 'none',
    placeItems: 'center',
  });

  const spinner = document.createElementNS(SVG_NAMESPACE, 'svg');
  spinner.dataset.paypercutWalletSpinner = '';
  spinner.setAttribute('viewBox', '0 0 24 24');
  spinner.setAttribute('aria-hidden', 'true');
  spinner.setAttribute('focusable', 'false');
  Object.assign(spinner.style, {
    display: 'block',
    width: '100%',
    height: '100%',
  });

  const track = document.createElementNS(SVG_NAMESPACE, 'circle');
  track.setAttribute('cx', '12');
  track.setAttribute('cy', '12');
  track.setAttribute('r', '9');
  track.setAttribute('fill', 'none');
  track.setAttribute('stroke', 'rgba(255, 255, 255, 0.28)');
  track.setAttribute('stroke-width', '2.5');

  const arc = document.createElementNS(SVG_NAMESPACE, 'circle');
  arc.setAttribute('cx', '12');
  arc.setAttribute('cy', '12');
  arc.setAttribute('r', '9');
  arc.setAttribute('fill', 'none');
  arc.setAttribute('stroke', '#fff');
  arc.setAttribute('stroke-width', '2.5');
  arc.setAttribute('stroke-linecap', 'round');
  arc.setAttribute('stroke-dasharray', '18 39');

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReducedMotion) {
    const rotation = document.createElementNS(SVG_NAMESPACE, 'animateTransform');
    rotation.setAttribute('attributeName', 'transform');
    rotation.setAttribute('type', 'rotate');
    rotation.setAttribute('from', '0 12 12');
    rotation.setAttribute('to', '360 12 12');
    rotation.setAttribute('dur', '0.85s');
    rotation.setAttribute('repeatCount', 'indefinite');
    arc.append(rotation);
  }

  spinner.append(track, arc);
  progress.append(spinner);
  return progress;
};

/**
 * Owns the merchant-document shield shown while a provider-owned wallet
 * surface is open. The hidden controller still owns routing and cancellation;
 * this host exists in the merchant document because an iframe cannot cover or
 * make its ancestor page inert.
 */
export class ElementsWalletAttemptOverlay {
  private host: HTMLDivElement | null = null;
  private cancelButton: HTMLButtonElement | null = null;
  private isolatedSiblings = new Map<HTMLElement, IsolatedSibling>();
  private isolationObserver: MutationObserver | null = null;
  private previousBodyOverflow = '';
  private previousDocumentOverflow = '';
  private previouslyFocused: HTMLElement | null = null;
  private providerElement: HTMLElement | null = null;
  private cancelAttempt: (() => void) | null = null;
  private readonly keyHandler = (event: KeyboardEvent): void => {
    if (!this.isActive()) return;
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelButton?.focus({ preventScroll: true });
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.cancel();
  };

  show(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
    onCancel: () => void,
    providerElement?: HTMLElement | null,
  ): boolean {
    const active = activeWalletAttempt;
    if (active) {
      return (
        active.owner === this &&
        active.componentId === componentId &&
        active.requestId === requestId
      );
    }

    const host = this.ensureHost();
    activeWalletAttempt = { owner: this, componentId, requestId };
    this.cancelAttempt = onCancel;
    this.providerElement = providerElement ?? null;
    this.previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.updateCopy(wallet, 'provider_open');
    document.body.prepend(host);
    host.hidden = false;
    host.style.display = 'flex';
    this.lockMerchantPage(host);
    document.addEventListener('keydown', this.keyHandler, true);
    this.cancelButton?.focus({ preventScroll: true });
    return true;
  }

  setPhase(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
    phase: WalletAttemptOverlayPhase,
  ): void {
    if (!this.matches(componentId, requestId)) return;
    this.updateCopy(wallet, phase);
  }

  hide(componentId: string, requestId: string): void {
    if (!this.matches(componentId, requestId)) return;
    this.hideHost();
  }

  close(componentId: string, requestId: string): void {
    if (!this.matches(componentId, requestId)) return;
    activeWalletAttempt = null;
    this.cancelAttempt = null;
    this.providerElement = null;
    this.hideHost();
  }

  destroy(): void {
    if (activeWalletAttempt?.owner === this) activeWalletAttempt = null;
    this.cancelAttempt = null;
    this.providerElement = null;
    this.hideHost();
    this.host?.remove();
    this.host = null;
    this.cancelButton = null;
  }

  private isActive(): boolean {
    return activeWalletAttempt?.owner === this;
  }

  private matches(componentId: string, requestId: string): boolean {
    return (
      activeWalletAttempt?.owner === this &&
      activeWalletAttempt.componentId === componentId &&
      activeWalletAttempt.requestId === requestId
    );
  }

  private cancel(): void {
    if (!this.isActive()) return;
    this.cancelAttempt?.();
  }

  private ensureHost(): HTMLDivElement {
    if (this.host) return this.host;
    const host = document.createElement('div');
    host.dataset.paypercutElementsWalletOverlay = '';
    host.hidden = true;
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-modal', 'true');
    host.setAttribute('aria-labelledby', 'paypercut-wallet-overlay-title');
    host.setAttribute('aria-describedby', 'paypercut-wallet-overlay-description');
    host.tabIndex = -1;
    Object.assign(host.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      margin: '0',
      paddingTop: 'max(20px, env(safe-area-inset-top))',
      paddingRight: 'max(20px, env(safe-area-inset-right))',
      paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
      paddingLeft: 'max(20px, env(safe-area-inset-left))',
      border: '0',
      outline: 'none',
      boxSizing: 'border-box',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto',
      overscrollBehavior: 'contain',
      background: 'rgba(0, 0, 0, 0.85)',
      color: '#fff',
      colorScheme: 'dark',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      zIndex: String(FALLBACK_Z_INDEX),
    });

    const content = document.createElement('div');
    Object.assign(content.style, {
      width: 'min(430px, 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    });

    const badge = document.createElement('div');
    badge.dataset.paypercutWalletBadge = '';
    badge.dataset.paypercutWalletOverlayTile = '';
    Object.assign(badge.style, {
      width: 'min(165.52107px, 46vw)',
      aspectRatio: PROVIDER_MARK_CANVAS_ASPECT_RATIO,
      padding: '0',
      border: '0',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'visible',
      background: 'transparent',
      boxShadow: 'none',
    });

    const title = document.createElement('h2');
    title.id = 'paypercut-wallet-overlay-title';
    Object.assign(title.style, {
      margin: '0',
      fontSize: 'clamp(26px, 5vw, 30px)',
      lineHeight: '1.2',
      fontWeight: '650',
      letterSpacing: '-0.02em',
    });

    const description = document.createElement('p');
    description.id = 'paypercut-wallet-overlay-description';
    Object.assign(description.style, {
      maxWidth: '430px',
      margin: `${PROVIDER_TEXT_GAP} 0 0`,
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: 'clamp(16px, 3.6vw, 18px)',
      lineHeight: '1.45',
      fontWeight: '400',
    });

    const progress = createProcessingIndicator();

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel payment';
    cancel.addEventListener('click', () => this.cancel());
    cancel.addEventListener('focus', () => {
      cancel.style.boxShadow = cancel.matches(':focus-visible')
        ? '0 0 0 3px rgba(138, 188, 255, 0.9)'
        : 'none';
    });
    cancel.addEventListener('blur', () => {
      cancel.style.boxShadow = 'none';
    });
    cancel.addEventListener('pointerenter', () => {
      cancel.style.background = 'rgba(132, 132, 132, 0.42)';
    });
    cancel.addEventListener('pointerleave', () => {
      cancel.style.background = 'rgba(111, 111, 111, 0.32)';
    });
    Object.assign(cancel.style, {
      width: 'min(260px, 100%)',
      minHeight: '44px',
      marginTop: PROVIDER_TEXT_GAP,
      padding: '10px 18px',
      border: '0',
      borderRadius: '10px',
      background: 'rgba(111, 111, 111, 0.32)',
      color: '#fff',
      font: 'inherit',
      fontSize: 'clamp(16px, 3.6vw, 18px)',
      lineHeight: '1.35',
      fontWeight: '400',
      cursor: 'pointer',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      transition: 'background-color 120ms ease, box-shadow 120ms ease',
    });

    content.append(badge, title, description, progress, cancel);
    host.append(content);
    this.host = host;
    this.cancelButton = cancel;
    return host;
  }

  private updateCopy(wallet: ExpressCheckoutWallet, phase: WalletAttemptOverlayPhase): void {
    const name = walletName(wallet);
    const badge = this.host?.querySelector<HTMLElement>('[data-paypercut-wallet-badge]');
    const progress = this.host?.querySelector<HTMLElement>('[data-paypercut-wallet-progress]');
    const title = this.host?.querySelector<HTMLElement>('#paypercut-wallet-overlay-title');
    const description = this.host?.querySelector<HTMLElement>(
      '#paypercut-wallet-overlay-description',
    );
    if (this.host) {
      this.host.dataset.paypercutWalletOverlayPhase = phase;
      this.host.setAttribute('aria-busy', phase === 'processing' ? 'true' : 'false');
    }
    if (badge) {
      badge.replaceChildren(createWalletMark(wallet));
      badge.style.aspectRatio = PROVIDER_MARK_CANVAS_ASPECT_RATIO;
      badge.style.background = 'transparent';
      badge.style.boxShadow = 'none';
      badge.style.display = phase === 'provider_open' ? 'flex' : 'none';
    }
    if (title) {
      title.style.marginTop =
        phase === 'processing'
          ? '0'
          : wallet === 'apple_pay'
            ? APPLE_PAY_TITLE_MARGIN
            : GOOGLE_PAY_TITLE_MARGIN;
    }
    if (progress) progress.style.display = phase === 'processing' ? 'grid' : 'none';
    if (phase === 'processing') {
      if (title) title.textContent = 'Processing your payment';
      if (description) {
        description.textContent = `${name} returned your payment details. Please wait while we securely prepare the next step.`;
      }
      return;
    }
    if (title) title.textContent = `${name} is open`;
    if (description) {
      description.textContent = `Complete your payment in the open ${name} window, or close ${name} to continue paying another way.`;
    }
  }

  private lockMerchantPage(host: HTMLElement): void {
    this.previousBodyOverflow = document.body.style.overflow;
    this.previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const protectedElements = this.getProtectedElements(host);

    const isolate = (element: HTMLElement): void => {
      if (this.isolatedSiblings.has(element) || protectedElements.has(element)) {
        return;
      }
      this.isolatedSiblings.set(element, {
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden'),
        tabIndex: element.getAttribute('tabindex'),
      });
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    };

    for (const child of Array.from(document.body.children)) {
      if (child instanceof HTMLElement) isolate(child);
    }
    this.isolationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (node instanceof HTMLElement) isolate(node);
        }
      }
    });
    this.isolationObserver.observe(document.body, { childList: true });
  }

  private getProtectedElements(host: HTMLElement): Set<HTMLElement> {
    const protectedElements = new Set<HTMLElement>([host]);
    if (!this.providerElement) return protectedElements;
    for (let node: HTMLElement | null = this.providerElement; node; node = node.parentElement) {
      protectedElements.add(node);
    }
    return protectedElements;
  }

  private hideHost(): void {
    if (typeof document === 'undefined') return;
    document.removeEventListener('keydown', this.keyHandler, true);
    this.isolationObserver?.disconnect();
    this.isolationObserver = null;
    for (const snapshot of this.isolatedSiblings.values()) {
      if (!snapshot.element.isConnected) continue;
      snapshot.element.inert = snapshot.inert;
      restoreAttribute(snapshot.element, 'aria-hidden', snapshot.ariaHidden);
      restoreAttribute(snapshot.element, 'tabindex', snapshot.tabIndex);
    }
    this.isolatedSiblings.clear();
    document.body.style.overflow = this.previousBodyOverflow;
    document.documentElement.style.overflow = this.previousDocumentOverflow;
    if (this.host) {
      this.host.hidden = true;
      this.host.style.display = 'none';
    }
    if (this.previouslyFocused?.isConnected) this.previouslyFocused.focus({ preventScroll: true });
    this.previouslyFocused = null;
  }
}
